import React, { PureComponent, useContext } from 'react';
import { createStructuredSelector } from 'reselect';
import { createSelector } from 'app/utils/reselect-legacy';
import { Playback, IPlaybackOutlet } from '../../services/rtc/Playback';
import EventMapEmitter, { AsEventMap } from '../../utils/EventMapEmitter';
import { printDebugTree } from '../../utils/debugTree';

export enum PlaybackState {
  /** Browser blocked playback, even muted */
  Blocked = 'blocked',
  /** Browser allowed for muted autoplay */
  PlayingWithBlockedSound = 'playingWithBlockedSound',
  /** Playing with sound */
  Playing = 'playing',
  /** Playing with sound muted by the user */
  PlayingMuted = 'playingMuted',
}

export interface IPlaybackControl {
  state: PlaybackState;
  resume(): void;
  toggleSound(): void;
}

const PlaybackContext = React.createContext<Playback | undefined>(undefined);
const PlaybackControlContext = React.createContext<IPlaybackControl | undefined>(undefined);

export const GetPeersPlayback = PlaybackContext.Consumer;
export const GetPeersPlaybackControl = PlaybackControlContext.Consumer;
export const usePeersPlayback = () => useContext(PlaybackContext);
export const usePeersPlaybackControl = () => useContext(PlaybackControlContext);

interface IState {
  readonly status: PlaybackState;
}

/**
 * Provides context shared between all `<video>` and `<audio>` elements
 * that play peers' video and audio streams. They're supposed to always
 * be autoplaying. In case of blocked auto playback, they're supposed
 * to call `onVideoBlocked` or `onSoundBlocked`. This will notify
 * `<PlaybackErrorMessage>` to show a `Play` or `Unmute` button.
 */
export default class PeersPlaybackProvider
extends PureComponent<unknown, IState> {
  public state = {
    status: PlaybackState.Playing,
  };

  private readonly getControlInterface: (that: PeersPlaybackProvider) => IPlaybackControl = createStructuredSelector({
    resume: (that: PeersPlaybackProvider) => that.resume,
    toggleSound: (that: PeersPlaybackProvider) => that.toggleSound,
    state: (that: PeersPlaybackProvider) => that.state.status,
    // Use legacy createSelector because we pass a mutable object (`this`) to this selector
  }, createSelector);

  private readonly playback: Playback = {
    addPlaybackOutlet: (outlet) => this.emitter.bindEvents(outlet),
    removePlaybackOutlet: (outlet) => this.emitter.unbindEvents(outlet),
    onSoundBlocked: () => this.setState(({ status: prev }) =>
      prev === PlaybackState.Blocked || prev === PlaybackState.PlayingWithBlockedSound
      ? null
      : { status: PlaybackState.PlayingWithBlockedSound },
    ),
    onVideoBlocked: () => this.setState(({ status: prev }) =>
      prev === PlaybackState.Blocked
      ? null
      : { status: PlaybackState.Blocked },
    ),
    // Propagate muted state and autoplay
    onLoadedMetaData: () => {
      const muted = this.state.status === PlaybackState.PlayingMuted;

      // Optimistically assume that the operation was successful. Any
      // blocked playback should be reported back via `onSoundBlocked`
      // and `onVideoBlocked` callbacks.
      this.setState(({ status }) =>
        muted && status === PlaybackState.PlayingMuted ? null :
        !muted && status === PlaybackState.Playing ? null :
        { status: PlaybackState.Playing },
      );

      this.emitter.emit('mute', muted);
      this.emitter.emit('play', null);
    },
  };

  private readonly emitter = new EventMapEmitter<AsEventMap<IPlaybackOutlet>>();

  public componentDidUpdate(prevProps: unknown, prevState: IState) {
    printDebugTree('peersPlayback', prevState, this.state);
  }

  public render() {
    const {
      props: { children },
      getControlInterface,
      playback,
    } = this;

    return (
      <PlaybackControlContext.Provider value={getControlInterface(this)}>
        <PlaybackContext.Provider value={playback}>
          {children}
        </PlaybackContext.Provider>
      </PlaybackControlContext.Provider>
    );
  }

  private resume = () => {
    // Optimistically change state to playing, any blocked playback
    // will put it to one of the error states
    this.setState({ status: PlaybackState.Playing });
    this.emitter.emit('play', null);
  };

  private toggleSound = () => {
    const { status } = this.state;
    if (status === PlaybackState.PlayingMuted) {
      this.emitter.emit('mute', false);
      this.resume();
    } else if (status === PlaybackState.Playing) {
      // Optimistically change to playing muted
      this.setState({ status: PlaybackState.PlayingMuted });
      this.emitter.emit('mute', true);
    }
  };
}
